// IndexedDB-backed local storage for report/audit drafts + queued photo uploads.
// Purpose: protect in-progress work (answers, notes, scores, photos) against lost
// connectivity or an accidental reload/crash while a report is still being completed.
// Nothing here touches submission/approval logic — it only persists data locally
// until it can be synced to the server.
const DB_NAME = "pr_offline_drafts";
const DB_VERSION = 1;
const STORE_DRAFTS = "drafts";
const STORE_PHOTOS = "photo_queue";

let dbPromise = null;

function openDb() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        if (!window.indexedDB) { reject(new Error("IndexedDB not available")); return; }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
                db.createObjectStore(STORE_DRAFTS, { keyPath: "key" });
            }
            if (!db.objectStoreNames.contains(STORE_PHOTOS)) {
                const store = db.createObjectStore(STORE_PHOTOS, { keyPath: "id", autoIncrement: true });
                store.createIndex("draftKey", "draftKey", { unique: false });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return dbPromise;
}

// Save (or update) the draft record for `key`. `dirty=true` means it hasn't
// been synced to the server yet — used on reload to decide whether to
// restore local data over the server's copy.
export async function saveDraft(key, data, dirty = true) {
    try {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_DRAFTS, "readwrite");
            tx.objectStore(STORE_DRAFTS).put({ key, data, dirty, updatedAt: Date.now() });
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    } catch { /* best-effort — IndexedDB unavailable (e.g. private mode) */ }
}

export async function getDraft(key) {
    try {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_DRAFTS, "readonly");
            const req = tx.objectStore(STORE_DRAFTS).get(key);
            let result;
            req.onsuccess = () => { result = req.result; };
            req.onerror = () => reject(req.error);
            tx.oncomplete = () => resolve(result || null);
        });
    } catch { return null; }
}

export async function clearDraft(key) {
    try {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_DRAFTS, "readwrite");
            tx.objectStore(STORE_DRAFTS).delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    } catch { /* no-op */ }
}

// Queue a photo for later upload. Stored as a raw ArrayBuffer, NOT a Blob — Mac
// Safari has a known WebKit bug where a Blob read back out of IndexedDB becomes
// unusable in a later fetch/XHR (the multipart body silently ends up with no file
// bytes, so the server sees "file" as missing). Returns the local queue id used to
// remove it once synced.
export async function queuePhoto(draftKey, meta, blob) {
    try {
        const buf = await blob.arrayBuffer();
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_PHOTOS, "readwrite");
            const req = tx.objectStore(STORE_PHOTOS).add({ draftKey, meta, buf, mimeType: blob.type || "image/jpeg", createdAt: Date.now() });
            let newId;
            req.onsuccess = () => { newId = req.result; };
            req.onerror = () => reject(req.error);
            tx.oncomplete = () => resolve(newId);
            tx.onerror = () => reject(tx.error);
        });
    } catch { return null; }
}

// A fresh Blob must be built from raw bytes right before use (see queuePhoto
// comment) — never hand a Blob that came straight out of IndexedDB to the network.
// Also recovers photos queued BEFORE this fix shipped, which were stored as a Blob
// directly: re-reading its bytes and building a brand new Blob breaks the stale
// WebKit reference just as effectively as the new ArrayBuffer path does.
async function hydratePhotoRecord(rec) {
    if (!rec) return rec;
    try {
        if (rec.buf) return { ...rec, blob: new Blob([rec.buf], { type: rec.mimeType || "image/jpeg" }) };
        if (rec.blob) {
            const buf = await rec.blob.arrayBuffer();
            return { ...rec, blob: new Blob([buf], { type: rec.blob.type || "image/jpeg" }) };
        }
    } catch { /* unreadable — fall through and hand back whatever we have */ }
    return rec;
}

// Every queued photo across EVERY record on this device, regardless of draftKey —
// used by the device-wide "Pending uploads" panel/badge and the app-level
// background flusher so a photo keeps uploading even after its own page is closed.
export async function getAllQueuedPhotos() {
    try {
        const db = await openDb();
        const rows = await new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_PHOTOS, "readonly");
            const req = tx.objectStore(STORE_PHOTOS).getAll();
            let result;
            req.onsuccess = () => { result = req.result; };
            req.onerror = () => reject(req.error);
            tx.oncomplete = () => resolve(result || []);
        });
        return Promise.all(rows.map(hydratePhotoRecord));
    } catch { return []; }
}

export async function getQueuedPhotos(draftKey) {
    try {
        const db = await openDb();
        const rows = await new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_PHOTOS, "readonly");
            const req = tx.objectStore(STORE_PHOTOS).index("draftKey").getAll(draftKey);
            let result;
            req.onsuccess = () => { result = req.result; };
            req.onerror = () => reject(req.error);
            tx.oncomplete = () => resolve(result || []);
        });
        return Promise.all(rows.map(hydratePhotoRecord));
    } catch { return []; }
}

export async function removeQueuedPhoto(photoId) {
    try {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_PHOTOS, "readwrite");
            tx.objectStore(STORE_PHOTOS).delete(photoId);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    } catch { /* no-op */ }
}

// Merge `patch` into a queued photo's stored meta (e.g. to record a failed
// attempt) WITHOUT touching the blob or removing it — used so a photo that
// keeps failing to upload stays safe on the device and visible to the user
// instead of silently vanishing.
export async function updateQueuedPhotoMeta(photoId, patch) {
    try {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_PHOTOS, "readwrite");
            const store = tx.objectStore(STORE_PHOTOS);
            const req = store.get(photoId);
            req.onsuccess = () => {
                const rec = req.result;
                if (rec) store.put({ ...rec, meta: { ...rec.meta, ...patch } });
            };
            req.onerror = () => reject(req.error);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    } catch { /* no-op */ }
}

// Persistent per-browser device id (survives reloads/tabs) — lets the server tell
// OTHER devices/users how many photos are still queued on a specific phone/laptop.
export function getDeviceId() {
    try {
        let v = localStorage.getItem("pr_device_id");
        if (!v) {
            v = (window.crypto?.randomUUID ? crypto.randomUUID() : `dev-${Date.now()}-${Math.random().toString(16).slice(2)}`);
            localStorage.setItem("pr_device_id", v);
        }
        return v;
    } catch { return "unknown-device"; }
}

// Short human-readable device summary, e.g. "iPhone Safari".
export function deviceLabel() {
    try {
        const ua = navigator.userAgent || "";
        const os = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Android/.test(ua) ? "Android"
            : /Macintosh/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : "Device";
        const browser = /CriOS/.test(ua) ? "Chrome" : /FxiOS/.test(ua) ? "Firefox"
            : /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Browser";
        return `${os} ${browser}`;
    } catch { return "Device"; }
}

// Best-effort: ask the browser not to evict our IndexedDB queue under storage
// pressure. No-op where unsupported; never throws.
export async function requestPersistentStorage() {
    try { await navigator.storage?.persist?.(); } catch { /* best-effort */ }
}
